import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, FileText, Download, BookOpen, Video, Calculator, Code, Sparkles, ChevronRight, BarChart3, Target, Users, Layers, ShieldCheck, Zap, TrendingUp, Building2, Award, Briefcase } from 'lucide-react';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

function FeaturedCard({ title, description, type, color, icon }) {
  const IconComp = icon;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow h-full flex flex-col">
      <div className={"p-6 " + color}>
        <IconComp size={32} className="text-white" />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <span className="text-xs font-medium text-[#2C39B8] bg-blue-50 px-2 py-1 rounded inline-block w-fit">{type}</span>
        <h3 className="text-lg font-bold text-gray-900 mt-3 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm mb-4 flex-1">{description}</p>
        <Link to="/contact" className="btn btn-primary text-sm inline-flex w-fit">
          <Download size={16} /> Request Access
        </Link>
      </div>
    </div>
  );
}

function ResourceRow({ title, description, type, icon, color, link }) {
  const IconComp = icon;
  return (
    <Link to={link || "/contact"} className="block bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-shadow">
      <div className="flex gap-4 items-start">
        <div className={"w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 " + color}>
          <IconComp size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{type}</span>
          </div>
          <h4 className="text-base font-bold text-gray-900 mb-1">{title}</h4>
          <p className="text-gray-600 text-sm line-clamp-2">{description}</p>
        </div>
        <div className="text-[#2C39B8] flex-shrink-0 mt-2">
          <ChevronRight size={20} />
        </div>
      </div>
    </Link>
  );
}

export default function ResourcesPage() {
  const [seoPages, setSeoPages] = useState([]);

  useEffect(function() {
    async function fetchSeoPages() {
      try {
        var res = await fetch(API_URL + '/api/seo/pages?limit=20');
        var data = await res.json();
        setSeoPages(data.pages || []);
      } catch (error) {
        console.error('Error fetching SEO pages:', error);
      }
    }
    fetchSeoPages();
  }, []);

  return (
    <div data-testid="resources-page">
      <Navbar />
      <SEOHead title="Resources - Channel Loyalty Playbooks, ROI Models & Industry Guides" description="Download B2B loyalty playbooks, ROI calculators, industry frameworks, and trade marketing guides. Expert resources for dealer, distributor, and retailer program design." keywords="loyalty program playbook, channel incentive ROI model, dealer loyalty guide, trade marketing framework" canonical="https://channelloyalty.ai/resources" />
      
      <main>
        {/* Hero */}
        <section className="hero-full" style={{ paddingBottom: '60px' }}>
          <div className="container">
            <div className="text-center pt-8 max-w-4xl mx-auto">
              <div className="hero-badge mx-auto mb-6">
                <Sparkles size={16} />
                B2B Loyalty Knowledge Hub
              </div>
              <h1 className="text-white mb-6">Channel Loyalty Resource Center</h1>
              <p className="text-white/80 text-lg mb-4">
                Frameworks, playbooks, ROI models, and industry benchmarks built for 
                channel loyalty, trade marketing, and partner incentive leaders.
              </p>
              <p className="text-white/60 text-sm">
                Used by 100+ enterprise brands running dealer, distributor, retailer, and influencer loyalty programs across India.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Resources - Top 3 Flagship */}
        <section className="section">
          <div className="container">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Flagship Resources</h2>
            <p className="text-gray-600 mb-8">Deep-dive frameworks for loyalty program design, launch, and optimization.</p>
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              <FeaturedCard 
                title="The B2B Channel Loyalty Playbook (2025 Edition)"
                description="The definitive guide to designing multi-tier loyalty programs. Covers dealer, distributor, wholesaler, and influencer program architectures. Includes reward mix frameworks, payout structures, and ROI calculators for 12 industry verticals."
                type="Playbook - 85 Pages"
                color="bg-[#2C39B8]"
                icon={BookOpen}
              />
              <FeaturedCard 
                title="Channel Incentive ROI Model & Calculator"
                description="Excel-based financial model to project program ROI before launch. Input your dealer base, avg. transaction value, target uplift %, and reward budget to get a 12-month P&L projection with break-even analysis and sensitivity testing."
                type="Financial Model - Excel"
                color="bg-emerald-600"
                icon={Calculator}
              />
              <FeaturedCard 
                title="Trade Marketing Budget Allocation Framework"
                description="How to split your trade marketing budget across dealer incentives, visibility programs, training, and digital loyalty. Includes benchmarks from 50+ Indian enterprises across cement, paints, FMCG, pharma, and auto parts."
                type="Framework - PDF"
                color="bg-pink-600"
                icon={BarChart3}
              />
            </div>

            {/* Program Design Guides */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Program Design Guides</h2>
            <p className="text-gray-600 mb-6">Step-by-step blueprints for designing loyalty programs by stakeholder type.</p>
            <div className="space-y-4 mb-16">
              <ResourceRow 
                title="Dealer Loyalty Program Design Guide"
                description="Target setting, slab structures, reward mix (cash vs. vouchers vs. experiences), KPI dashboards, and engagement cadence for dealer programs. Includes templates for dealer onboarding and quarterly business reviews."
                type="Design Guide"
                icon={Users}
                color="bg-blue-600"
                link="/solutions/dealer-loyalty-program"
              />
              <ResourceRow 
                title="Distributor Incentive Architecture"
                description="How to structure volume incentives, coverage bonuses, prompt payment rewards, and market development funds (MDF) for distributors. Covers counter-share tracking, beat planning integration, and distributor scorecard design."
                type="Architecture Blueprint"
                icon={Layers}
                color="bg-cyan-600"
                link="/solutions/distributor-loyalty-program"
              />
              <ResourceRow 
                title="Retailer Engagement & QR Loyalty Playbook"
                description="QR code scanning workflows, retailer tier mechanics, display incentive structures, and mystery audit integration. How to get retailers to scan, stay engaged, and become brand advocates."
                type="Playbook"
                icon={Target}
                color="bg-orange-600"
                link="/solutions/retailer-loyalty-program"
              />
              <ResourceRow 
                title="Influencer & Contractor Reward Program Guide"
                description="Engaging painters, masons, plumbers, electricians, and mechanics as brand influencers. WhatsApp-first enrollment, scan-and-earn mechanics, instant UPI payouts, and aspiration tier design for the trade influencer segment."
                type="Strategy Guide"
                icon={Award}
                color="bg-[#2C39B8]"
                link="/solutions/influencer-loyalty-program"
              />
              <ResourceRow 
                title="Wholesaler & C&F Loyalty Framework"
                description="Structuring loyalty for the wholesale and C&F tier. Bulk purchase incentives, credit period rewards, geography-based targets, and secondary sales tracking frameworks for the wholesale distribution layer."
                type="Framework"
                icon={Building2}
                color="bg-teal-600"
                link="/solutions/wholesaler-loyalty-program"
              />
            </div>

            {/* Industry-Specific Guides */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Industry Playbooks</h2>
            <p className="text-gray-600 mb-6">Loyalty strategies tailored for specific industry verticals and their unique channel dynamics.</p>
            <div className="space-y-4 mb-16">
              <ResourceRow 
                title="Cement Industry: Dealer + Mason Loyalty"
                description="Bag-level QR codes, mason engagement via missed-call enrollment, contractor tier programs, seasonal campaign multipliers during construction peaks (Oct-Mar), and counter-share tracking for multi-brand cement dealers."
                type="Industry Playbook"
                icon={Building2}
                color="bg-gray-700"
                link="/solutions/cement-dealer-loyalty"
              />
              <ResourceRow 
                title="Paints & Coatings: Painter + Dealer Programs"
                description="Painter loyalty via can-scanning, color consultant training incentives, dealer display rewards, tinting machine utilization bonuses, and festive season campaign frameworks for the decorative paint segment."
                type="Industry Playbook"
                icon={Sparkles}
                color="bg-green-600"
                link="/solutions/paints-dealer-loyalty"
              />
              <ResourceRow 
                title="FMCG: Retailer Trade Promotions"
                description="Scheme management for FMCG retail — display incentives, category-specific targets, festive stock-up rewards, planogram compliance tracking, and retailer segmentation by outlet type (kirana, modern trade, wholesale)."
                type="Industry Playbook"
                icon={TrendingUp}
                color="bg-green-600"
                link="/solutions/fmcg-retailer-loyalty"
              />
              <ResourceRow 
                title="Pharma Distribution: Stockist & Chemist Loyalty"
                description="Compliance-first loyalty for pharma channels. Stockist quarterly volume incentives, chemist counter-push programs, ethical promotion guidelines, and cold-chain adherence rewards for temperature-sensitive products."
                type="Industry Playbook"
                icon={ShieldCheck}
                color="bg-red-600"
                link="/solutions/pharma-distributor-loyalty"
              />
              <ResourceRow 
                title="Auto Parts & Lubricants: Mechanic Loyalty"
                description="Workshop-level engagement — mechanic scan-and-earn programs, garage owner incentives, oil-change tracking rewards, genuine parts authentication, and training certification programs for the automotive aftermarket."
                type="Industry Playbook"
                icon={Zap}
                color="bg-indigo-600"
                link="/solutions/auto-parts-dealer-loyalty"
              />
              <ResourceRow 
                title="Building Materials: Multi-Stakeholder Loyalty"
                description="Tiles, pipes, fittings, sanitaryware, and steel — multi-brand dealer rewards, plumber/contractor engagement, site-level tracking, project-based incentives, and architect specification programs."
                type="Industry Playbook"
                icon={Briefcase}
                color="bg-stone-600"
                link="/solutions/building-materials-loyalty"
              />
            </div>

            {/* Reward & Payout Resources */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Reward Strategy & Payout Guides</h2>
            <p className="text-gray-600 mb-6">Optimize your reward mix, payout infrastructure, and incentive economics.</p>
            <div className="space-y-4 mb-16">
              <ResourceRow 
                title="The Reward Mix Optimization Guide"
                description="How to balance cash rewards, gift vouchers, travel experiences, merchandise, insurance, and training credits. Reward preference data across dealer, distributor, retailer, and influencer segments by industry."
                type="Strategy Guide"
                icon={Award}
                color="bg-[#2C39B8]"
                link="/solutions/cash-rewards-program"
              />
              <ResourceRow 
                title="Instant UPI Payout Infrastructure Whitepaper"
                description="Technical architecture for sub-2-second reward payouts. UPI integration, KYC verification workflows, TDS compliance, payout reconciliation, and fraud prevention for high-volume B2B reward disbursement."
                type="Technical Whitepaper"
                icon={Zap}
                color="bg-green-600"
                link="/solutions/cash-rewards-program"
              />
              <ResourceRow 
                title="Gift Voucher & E-Gift Card Strategy"
                description="Category-wise voucher performance benchmarks (shopping, fuel, dining, travel). Procurement economics, digital vs. physical fulfillment, and voucher redemption optimization tactics."
                type="Strategy Document"
                icon={FileText}
                color="bg-pink-600"
                link="/solutions/gift-voucher-rewards"
              />
              <ResourceRow 
                title="Loyalty Trip & Travel Reward Playbook"
                description="Planning annual dealer loyalty trips. Budgeting, destination selection, qualification criteria, travel partner management, and post-trip engagement strategies to maximize program recall."
                type="Playbook"
                icon={BookOpen}
                color="bg-sky-600"
                link="/solutions/travel-rewards-program"
              />
            </div>

            {/* Technology & Implementation */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Technology & Implementation</h2>
            <p className="text-gray-600 mb-6">Technical guides for platform integration, data setup, and program launch.</p>
            <div className="space-y-4 mb-16">
              <ResourceRow 
                title="QR Code vs Invoice Upload: Implementation Comparison"
                description="When to use QR scanning, invoice upload (OCR), ERP integration, or bill scanning for transaction capture. Cost analysis, accuracy benchmarks, adoption rates, and hybrid approach recommendations."
                type="Comparison Guide"
                icon={Code}
                color="bg-blue-600"
                link="/solutions/qr-code-loyalty-program"
              />
              <ResourceRow 
                title="ERP/DMS Integration Handbook"
                description="Step-by-step integration with SAP, Oracle, Tally, and custom DMS platforms. API documentation, data mapping templates, sync frequency recommendations, and error handling best practices."
                type="Technical Guide"
                icon={Code}
                color="bg-gray-700"
                link="/solutions/b2b-loyalty-program"
              />
              <ResourceRow 
                title="AI-Powered Loyalty Analytics Whitepaper"
                description="Leveraging AI for predictive churn analysis, reward optimization, fraud detection, and conversational BI. How ChannelLoyalty's AI Brain transforms raw channel data into actionable growth intelligence."
                type="Whitepaper"
                icon={BarChart3}
                color="bg-[#2C39B8]"
                link="/blog/ai-analytics-channel-loyalty"
              />
              <ResourceRow 
                title="Channel Loyalty Program Launch Checklist"
                description="30-60-90 day launch plan. Pre-launch (data setup, reward sourcing, dealer communication), launch (enrollment drives, training, helpdesk setup), and post-launch (KPI tracking, iteration, scale-up)."
                type="Checklist - PDF"
                icon={ShieldCheck}
                color="bg-emerald-600"
                link="/contact"
              />
              <ResourceRow 
                title="ChannelLoyalty Platform API Documentation"
                description="Complete REST API reference for enrollment, transactions, rewards, payouts, and analytics endpoints. Includes webhook configurations, authentication, rate limits, and sandbox testing."
                type="API Documentation"
                icon={Code}
                color="bg-slate-700"
                link="/contact"
              />
            </div>
          </div>
        </section>

        {/* Dynamic SEO Pages Section */}
        {seoPages.length > 0 && (
          <section className="bg-gray-50 py-16">
            <div className="container">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Solution Guides by Industry & Stakeholder</h2>
              <p className="text-gray-600 mb-8">In-depth guides for specific loyalty program combinations across industries.</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {seoPages.slice(0, 12).map(function(page) {
                  return (
                    <Link 
                      key={page.slug} 
                      to={"/solutions/" + page.slug}
                      className="block p-4 bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all"
                    >
                      <div className="text-xs text-[#2C39B8] font-medium mb-1">{page.industry}</div>
                      <h4 className="text-sm font-bold text-gray-900 line-clamp-2 mb-2">{page.title}</h4>
                      <div className="text-xs text-gray-500">{page.stakeholder} Program</div>
                    </Link>
                  );
                })}
              </div>
              {seoPages.length > 12 && (
                <div className="text-center mt-8">
                  <Link to="/blog" className="text-[#2C39B8] hover:text-[#2C39B8] font-medium text-sm">
                    View All {seoPages.length}+ Solution Guides <ArrowRight size={14} className="inline ml-1" />
                  </Link>
                </div>
              )}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="cta-section">
          <div className="container relative z-10">
            <h2>Need a Custom Loyalty Strategy?</h2>
            <p>Our channel loyalty consultants will build a tailored program blueprint for your industry, stakeholders, and growth goals.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Talk to a Loyalty Expert <ArrowRight size={18} />
              </Link>
              <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
                <MessageCircle size={18} /> WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}
